import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Heart, Bell, RefreshCw, Clock } from "lucide-react";
import api from "../../api";
import { useFavourites } from "../../context/FavouritesContext";
import { useAuth } from "../../context/AuthContext";
import { useLocationFilter } from "../../context/LocationContext";
import { useLang } from "../../context/LanguageContext";
import { useToast } from "../../hooks/use-toast";
import { plausibleEvent } from "../../utils/track";
import Countdown from "./Countdown";
import { isLive } from "../../utils/countdown";
import { thumb } from "../../utils/image";

const RightRail = () => {
  const { isFav, toggleFav } = useFavourites();
  const { user } = useAuth();
  const { city, country, place, gps, followMode, setFollowMode } = useLocationFilter();
  const { t } = useLang();
  const { toast } = useToast();
  const [featured, setFeatured] = useState(null);
  const [expiry, setExpiry] = useState([]);
  // Mirror whatever the top-bar picker currently has, not the business's registered address.
  const locationLabel = (gps && (place || t("topbar.nearMe"))) || place || city || country || user?.location || t("topbar.anywhere");

  useEffect(() => {
    api.get("/deals").then((res) => {
      const list = res.data.filter(isLive);
      setFeatured(list.find((d) => d.featured) || list[0] || null);
      setExpiry(list.filter((d) => d.nearExpiry).slice(0, 3));
    }).catch(() => {});
  }, []);

  return (
    <div className="space-y-5 sticky top-4">
      <div className="bg-card border border-border rounded-2xl p-5 shadow-sm">
        <h3 className="font-bold text-foreground mb-3">{t("rail.yourLocation")}</h3>
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-sm text-muted-foreground min-w-0"><MapPin size={16} className="text-[#16a34a] shrink-0" /> <span className="truncate">{locationLabel}</span></span>
          <button onClick={() => window.dispatchEvent(new Event("lp-open-location"))} data-testid="rail-change-location" className="text-sm font-semibold text-[#16a34a] hover:underline shrink-0 ml-2">{t("rail.change")}</button>
        </div>
        {gps && (
          <div className="mt-2 flex items-center gap-2 text-xs" data-testid="follow-location-row">
            <span className="text-muted-foreground">
              {followMode === "off" ? t("loc.notFollowing") : followMode === "live" ? t("loc.followingLive") : t("loc.following")}
            </span>
            <button
              onClick={() => setFollowMode(followMode === "off" ? "refresh" : "off")}
              data-testid="follow-location-toggle"
              className="font-bold text-[#16a34a] hover:underline">
              {followMode === "off" ? t("loc.turnOn") : t("loc.turnOff")}
            </button>
            {followMode !== "off" && (
              <button
                onClick={() => setFollowMode(followMode === "live" ? "refresh" : "live")}
                data-testid="follow-location-live"
                className="font-bold text-muted-foreground hover:text-[#16a34a]">
                {followMode === "live" ? t("loc.stopLive") : t("loc.goLive")}
              </button>
            )}
          </div>
        )}
      </div>

      {featured && (
        <div className="bg-card border border-border rounded-2xl p-5 shadow-sm">
          <h3 className="font-bold text-foreground mb-3">{t("rail.featuredDeal")}</h3>
          <div className="relative rounded-xl overflow-hidden mb-3">
            <img src={thumb(featured.image, 480)} alt={featured.business} loading="lazy" decoding="async" className="w-full h-36 object-cover" />
            <span className="absolute top-2 left-2 bg-rose-500 text-white text-[11px] font-bold px-2.5 py-1 rounded-md">FEATURED</span>
            <button onClick={() => toggleFav(featured.id)} className="absolute top-2 right-2 h-8 w-8 rounded-full bg-white/90 flex items-center justify-center" aria-label="Favourite"><Heart size={16} className={isFav(featured.id) ? "fill-rose-500 text-rose-500" : "text-slate-600"} /></button>
          </div>
          <h4 className="font-bold text-foreground">{featured.business}</h4>
          <p className="text-sm text-muted-foreground">{featured.title}</p>
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1 mb-3"><MapPin size={13} className="text-[#16a34a]" /> {featured.location}</p>
          <Link to={`/deal/${featured.id}`} className="block text-center bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-semibold py-2.5 rounded-xl transition-colors">{t("rail.viewDeal")}</Link>
        </div>
      )}

      <div className="bg-card border border-border rounded-2xl p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-foreground">{t("rail.nearExpiry")} <span className="text-rose-500">{t("rail.lastChance")}</span></h3>
          <Link to="/near-expiry" className="text-sm font-semibold text-[#16a34a] hover:underline">{t("rail.viewAll")}</Link>
        </div>
        <div className="space-y-4">
          {expiry.map((it) => (
            <Link to={`/deal/${it.id}`} key={it.id} className="flex items-center gap-3 group">
              <img src={thumb(it.image, 96)} alt={it.title} loading="lazy" decoding="async" className="h-14 w-14 rounded-xl object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-foreground text-sm truncate group-hover:text-[#16a34a]">{it.title}</p>
                <p className="text-xs text-muted-foreground truncate">{it.business}</p>
                <p className="text-xs text-orange-500 font-medium flex items-center gap-1"><Clock size={11} /> <Countdown expiresAt={it.expiresAt} fallback={it.timeLeft} /></p>
                <p className="text-sm mt-0.5"><span className="font-bold text-foreground">${Number(it.price).toFixed(2)}</span>{" "}{it.was != null && <span className="text-muted-foreground line-through text-xs">${Number(it.was).toFixed(2)}</span>}</p>
              </div>
              <span className="self-start bg-[#e7f6ec] text-[#16a34a] text-[11px] font-bold px-2 py-1 rounded-md">{it.discount}% OFF</span>
            </Link>
          ))}
          {expiry.length === 0 && <p className="text-sm text-muted-foreground">{t("rail.noNearExpiry")}</p>}
        </div>
      </div>

      <div className="bg-[#e7f6ec] dark:bg-[#16341f] rounded-2xl p-5">
        <div className="flex items-start gap-3">
          <span className="h-10 w-10 rounded-xl bg-white/70 dark:bg-white/10 flex items-center justify-center shrink-0"><Bell size={20} className="text-[#16a34a]" /></span>
          <div className="flex-1">
            <h4 className="font-bold text-foreground">{t("rail.getAlerts")}</h4>
            <p className="text-xs text-muted-foreground mb-3">{t("rail.getAlertsDesc")}</p>
            <button onClick={() => { plausibleEvent("Alerts Enabled"); toast({ title: t("rr.alertsEnabled"), description: t("rr.youLlBeNotified") }); }} className="inline-flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors"><RefreshCw size={14} /> {t("rail.enableAlerts")}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RightRail;
